// Путь: D:\QR\QR-WEB\dashboard\src\components\Business\CampaignPhotoReviewStep.jsx
// Название: Campaign Photo Review Step - Full Featured
// Версия: 3.0.0
// Дата: 23.12.2025

import React, { useState, useEffect, useRef } from 'react';
import { api } from '../../services/api';

const CampaignPhotoReviewStep = ({ campaignId, onNext, onBack }) => {
  const [recipients, setRecipients] = useState([]);
  const [stats, setStats] = useState({ total: 0, with_photo: 0, pending_photo: 0, ready: 0 });
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Модальное окно
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [photoUrl, setPhotoUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  
  // Превью
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  
  // Drag & drop
  const [dragOver, setDragOver] = useState(null);
  
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadRecipients();
  }, [campaignId]);

  const loadRecipients = async () => {
    try {
      const response = await api.get('/campaigns/' + campaignId + '/recipients');
      const data = response.data || response;
      const recipientsList = data.recipients || data || [];
      setRecipients(recipientsList);
      
      // Считаем статистику
      const total = recipientsList.length;
      const withPhoto = recipientsList.filter(r => r.photo_url).length;
      const ready = recipientsList.filter(r => r.status === 'ready').length;
      setStats({
        total,
        with_photo: withPhoto,
        pending_photo: total - withPhoto,
        ready
      });
    } catch (err) {
      console.error('Load recipients error:', err);
      setError('Ошибка загрузки получателей');
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // АВТОПОИСК ФОТО
  // ============================================
  
  const handleSearchAllPhotos = async () => {
    setSearching(true);
    setError('');
    setSuccess('');
    try {
      const response = await api.post('/campaigns/' + campaignId + '/search-photos');
      const data = response.data || response;
      await loadRecipients();
      setSuccess('Поиск завершен. Найдено: ' + (data.found || 0) + ' фото');
    } catch (err) {
      console.error('Search photos error:', err);
      setError('Ошибка поиска фото');
    } finally {
      setSearching(false);
    }
  };

  const handleSearchOnePhoto = async (recipientId) => {
    try {
      await api.post('/campaigns/' + campaignId + '/search-photos', { recipientId });
      await loadRecipients();
    } catch (err) {
      console.error('Search photo error:', err);
    }
  };

// ============================================
  // ЗАГРУЗКА ФОТО
  // ============================================
  
  const openPhotoModal = (recipient) => {
    setSelectedRecipient(recipient);
    setPhotoUrl(recipient.photo_url || '');
    setModalOpen(true);
  };

  const handleUrlSubmit = async () => {
    if (!photoUrl.trim() || !selectedRecipient) return;
    
    setUploading(true);
    setError('');
    try {
      await api.put('/campaigns/recipients/' + selectedRecipient.id + '/photo', {   
        photoUrl: photoUrl.trim(),
        skipValidation: true		
      });
      await loadRecipients();
      setModalOpen(false);
      setSuccess('Фото обновлено');
    } catch (err) {
      console.error('URL submit error:', err);
      setError('Ошибка сохранения фото');
    } finally {
      setUploading(false);
    }
  };

  const handleFileUpload = async (recipientId, file) => {
    if (!file) return;
    
    // Проверка типа файла
    if (!file.type.startsWith('image/')) {
      setError('Выберите изображение');
      return;
    }
    
    // Проверка размера (макс 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Максимальный размер файла 5MB');
      return;
    }
    
    setUploading(true);
    setError('');
    
    try {
      const formData = new FormData();
      formData.append('photo', file);
      
      const response = await fetch(
        'https://qr-money.onrender.com/api/campaigns/recipients/' + recipientId + '/photo/upload',
        {
          method: 'POST',
          headers: {
            'Authorization': 'Bearer ' + localStorage.getItem('authToken')
          },
          body: formData
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Upload failed');
      }
      
      await loadRecipients();
      setModalOpen(false);
      setSuccess('Фото загружено');
    } catch (err) {
      console.error('Upload photo error:', err);
      setError('Ошибка загрузки файла: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  // ============================================
  // УДАЛЕНИЕ ФОТО
  // ============================================
  
  const handleDeletePhoto = async (recipientId) => {
    if (!window.confirm('Удалить фото?')) return;
    
    try {
      await api.delete('/campaigns/recipients/' + recipientId + '/photo');
      await loadRecipients();
      setSuccess('Фото удалено');
    } catch (err) {
      setError('Ошибка удаления');
    }
  };

  const handleDeleteAllPhotos = async () => {
    if (!window.confirm('Удалить все фото? Это действие нельзя отменить.')) return;
    
    try {
      for (const r of recipients.filter(r => r.photo_url)) {
        await api.delete('/campaigns/recipients/' + r.id + '/photo');
      }
      await loadRecipients();
      setSuccess('Все фото удалены');
    } catch (err) {
      setError('Ошибка удаления');
    }
  };

  // ============================================
  // DRAG & DROP
  // ============================================
  
  const handleDragOver = (e, recipientId) => {
    e.preventDefault();
    setDragOver(recipientId);
  };

  const handleDragLeave = () => {
    setDragOver(null);
  };

  const handleDrop = async (e, recipientId) => {
    e.preventDefault();
    setDragOver(null);
    
    const file = e.dataTransfer.files[0];
    if (file) {
      await handleFileUpload(recipientId, file);
    }
  };

  // ============================================
  // ПРЕВЬЮ
  // ============================================
  
  const openPreview = (url) => {
    setPreviewUrl(url);
    setPreviewOpen(true);
  };

  // ============================================
  // ПРОПУСК БЕЗ ФОТО
  // ============================================
  
  const handleSkipPhotos = () => {
    if (recipients.length === 0) {
      setError('Нет получателей');
      return;
    }
    onNext();
  };

  const canProceed = recipients.length > 0;

  // ============================================
  // СТИЛИ
  // ============================================
  
  const styles = {
    container: { maxWidth: 950 },
    header: { marginBottom: 24 },
    title: { fontSize: 22, fontWeight: 600, marginBottom: 8 },
    desc: { color: '#666', fontSize: 14 },
    
    statsBar: { 
      display: 'flex', gap: 16, marginBottom: 24, background: '#f8fafc', 
      padding: 16, borderRadius: 12 
    },
    statItem: { 
      flex: 1, textAlign: 'center', padding: '12px 8px', 
      background: 'white', borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' 
    },
    statValue: { fontSize: 24, fontWeight: 700, marginBottom: 4 },
    statLabel: { fontSize: 12, color: '#666' },
    
    actions: { display: 'flex', gap: 10, marginBottom: 24, flexWrap: 'wrap' },
    actionBtn: { 
      padding: '10px 16px', borderRadius: 8, fontSize: 14, fontWeight: 500, 
      cursor: 'pointer', border: 'none', display: 'flex', alignItems: 'center', gap: 6 
    },
    searchBtn: { background: '#dbeafe', color: '#1d4ed8' },
    deleteAllBtn: { background: '#fee2e2', color: '#dc2626' },
    
    tableWrap: { 
      border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden', 
      maxHeight: 450, overflowY: 'auto' 
    },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: { 
      padding: '14px 12px', textAlign: 'left', background: '#f9fafb', 
      fontWeight: 600, fontSize: 13, color: '#374151', position: 'sticky', top: 0 
    },
    td: { padding: '12px', borderBottom: '1px solid #f3f4f6', verticalAlign: 'middle' },
    
    avatarWrap: { position: 'relative', width: 56, height: 56 },
    avatar: { 
      width: 56, height: 56, borderRadius: '50%', objectFit: 'cover', 
      border: '2px solid #e5e7eb', cursor: 'pointer' 
    },
    avatarEmpty: { 
      width: 56, height: 56, borderRadius: '50%', background: '#f3f4f6', 
      display: 'flex', alignItems: 'center', justifyContent: 'center', 
      fontSize: 24, border: '2px dashed #d1d5db', cursor: 'pointer' 
    },
    avatarDragOver: { borderColor: '#10B981', background: '#ecfdf5' },
    
    name: { fontWeight: 500 },
    email: { color: '#6b7280', fontSize: 13 },
    phone: { color: '#6b7280', fontSize: 13 },
    source: { fontSize: 12, color: '#9ca3af' },
    
    badge: { 
      padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 500, 
      display: 'inline-block' 
    },
    badgeReady: { background: '#dcfce7', color: '#16a34a' },
    badgePending: { background: '#fef3c7', color: '#ca8a04' },
    badgeNoPhoto: { background: '#fee2e2', color: '#dc2626' },
    
    tdActions: { display: 'flex', gap: 6 },
    iconBtn: { 
      width: 32, height: 32, borderRadius: 6, border: 'none', 
      cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' 
    },
    
    footer: { 
      display: 'flex', gap: 12, paddingTop: 24, marginTop: 24, 
      borderTop: '1px solid #e5e7eb' 
    },
    btnBack: { 
      flex: 1, padding: '14px 24px', background: '#f3f4f6', border: 'none', 
      borderRadius: 10, fontSize: 15, cursor: 'pointer', fontWeight: 500 
    },
    btnNext: { 
      flex: 2, padding: '14px 24px', background: 'linear-gradient(135deg, #10B981, #059669)', 
      color: 'white', border: 'none', borderRadius: 10, fontSize: 15, 
      fontWeight: 600, cursor: 'pointer' 
    },
    btnSkip: { 
      padding: '14px 24px', background: '#fef3c7', color: '#92400e', 
      border: 'none', borderRadius: 10, fontSize: 15, cursor: 'pointer' 
    },
    btnDisabled: { background: '#d1d5db', cursor: 'not-allowed' },
    
    alert: { padding: '12px 16px', borderRadius: 8, marginBottom: 16, fontSize: 14 },
    alertError: { background: '#fee2e2', color: '#dc2626' },
    alertSuccess: { background: '#dcfce7', color: '#16a34a' },
    
    modal: { 
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, 
      background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', 
      justifyContent: 'center', zIndex: 1000 
    },
    modalContent: { 
      background: 'white', borderRadius: 16, padding: 24, width: 420, 
      maxWidth: '90vw', maxHeight: '90vh', overflow: 'auto' 
    },
    modalTitle: { fontSize: 18, fontWeight: 600, marginBottom: 20 },
    modalInput: { 
      width: '100%', padding: '12px 14px', border: '2px solid #e5e7eb', 
      borderRadius: 8, fontSize: 14, marginBottom: 12, boxSizing: 'border-box' 
    },
    modalDivider: { 
      textAlign: 'center', color: '#9ca3af', margin: '16px 0', 
      fontSize: 13, position: 'relative' 
    },
    uploadZone: { 
      border: '2px dashed #d1d5db', borderRadius: 12, padding: 32, 
      textAlign: 'center', cursor: 'pointer', marginBottom: 16, 
      transition: 'all 0.2s' 
    },
    uploadZoneActive: { borderColor: '#10B981', background: '#ecfdf5' },
    modalBtns: { display: 'flex', gap: 10, marginTop: 20 },
    
    preview: { 
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, 
      background: 'rgba(0,0,0,0.9)', display: 'flex', alignItems: 'center', 
      justifyContent: 'center', zIndex: 1001, cursor: 'pointer' 
    },
    previewImg: { maxWidth: '90vw', maxHeight: '90vh', borderRadius: 8 },
    
    empty: { textAlign: 'center', padding: 60, color: '#6b7280' },
    emptyIcon: { fontSize: 48, marginBottom: 16 }
  };

  // ============================================
  // РЕНДЕР
  // ============================================

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={{ textAlign: 'center', padding: 60 }}>
          <p>⏳ Загрузка получателей...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>📷 Проверка фотографий</h2>
        <p style={styles.desc}>
          Добавьте фото получателей для персонализации AI-подарков. 
          Можно загрузить вручную или найти автоматически.
        </p>
      </div>

      {/* Статистика */}
      <div style={styles.statsBar}>
        <div style={styles.statItem}>
          <div style={{...styles.statValue, color: '#374151'}}>{stats.total}</div>
          <div style={styles.statLabel}>Всего</div>
        </div>
        <div style={styles.statItem}>
          <div style={{...styles.statValue, color: '#16a34a'}}>{stats.with_photo}</div>
          <div style={styles.statLabel}>С фото</div>
        </div>
        <div style={styles.statItem}>
          <div style={{...styles.statValue, color: '#dc2626'}}>{stats.pending_photo}</div>
          <div style={styles.statLabel}>Без фото</div>
        </div>
        <div style={styles.statItem}>
          <div style={{...styles.statValue, color: '#2563eb'}}>{stats.ready}</div>
          <div style={styles.statLabel}>Готово</div>
        </div>
      </div>

      {/* Уведомления */}
      {error && (
        <div style={{...styles.alert, ...styles.alertError}}>
          ❌ {error}
          <button onClick={() => setError('')} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>
        </div>
      )}
      {success && (
        <div style={{...styles.alert, ...styles.alertSuccess}}>
          ✅ {success}
          <button onClick={() => setSuccess('')} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>
        </div>
      )}

      {/* Действия */}
      <div style={styles.actions}>
        <button 
          style={{...styles.actionBtn, ...styles.searchBtn}} 
          onClick={handleSearchAllPhotos}
          disabled={searching}
        >
          {searching ? '⏳ Поиск...' : '🔍 Найти фото автоматически'}
        </button>
        {stats.with_photo > 0 && (
          <button 
            style={{...styles.actionBtn, ...styles.deleteAllBtn}} 
            onClick={handleDeleteAllPhotos}
          >
            🗑️ Удалить все фото
          </button>
        )}
      </div>

      {/* Таблица */}
      {recipients.length === 0 ? (
        <div style={styles.empty}>
          <div style={styles.emptyIcon}>📭</div>
          <p>Нет получателей</p>
          <p style={{ fontSize: 13 }}>Вернитесь назад и добавьте получателей</p>
        </div>
      ) : (
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Фото</th>
                <th style={styles.th}>Получатель</th>
                <th style={styles.th}>Контакты</th>
                <th style={styles.th}>Источник</th>
                <th style={styles.th}>Статус</th>
                <th style={styles.th}>Действия</th>
              </tr>
            </thead>
            <tbody>
              {recipients.map(r => (
                <tr key={r.id}>
                  <td style={styles.td}>
                    <div 
                      style={styles.avatarWrap}
                      onDragOver={(e) => handleDragOver(e, r.id)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, r.id)}
                    >
                      {r.photo_url ? (
                        <img 
                          src={r.photo_url} 
                          alt={r.name}
                          style={styles.avatar}
                          onClick={() => openPreview(r.photo_url)}
                        />
                      ) : (
                        <div 
                          style={{
                            ...styles.avatarEmpty,
                            ...(dragOver === r.id ? styles.avatarDragOver : {})
                          }}
                          onClick={() => openPhotoModal(r)}
                        >
                          {dragOver === r.id ? '📥' : '👤'}
                        </div>
                      )}
                    </div>
                  </td>
                  <td style={styles.td}>
                    <div style={styles.name}>{r.name}</div>
                  </td>
                  <td style={styles.td}>
                    <div style={styles.email}>{r.email || '-'}</div>
                    <div style={styles.phone}>{r.phone || ''}</div>
                  </td>
                  <td style={styles.td}>
                    <span style={styles.source}>{r.photo_source || '-'}</span>
                  </td>
                  <td style={styles.td}>
                    <span style={{
                      ...styles.badge,
                      ...(r.photo_url ? styles.badgeReady : styles.badgeNoPhoto)
                    }}>
                      {r.photo_url ? '✓ Есть фото' : '✗ Нет фото'}
                    </span>
                  </td>
                  <td style={styles.td}>
                    <div style={styles.tdActions}>
                      <button 
                        style={{...styles.iconBtn, background: '#dbeafe', color: '#1d4ed8'}}
                        onClick={() => openPhotoModal(r)}
                        title="Загрузить фото"
                      >
                        📷
                      </button>
                      <button 
                        style={{...styles.iconBtn, background: '#e0e7ff', color: '#4338ca'}}
                        onClick={() => handleSearchOnePhoto(r.id)}
                        title="Найти автоматически"
                      >
                        🔍
                      </button>
                      {r.photo_url && (
                        <button 
                          style={{...styles.iconBtn, background: '#fee2e2', color: '#dc2626'}}
                          onClick={() => handleDeletePhoto(r.id)}
                          title="Удалить фото"
                        >
                          🗑️
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Футер */}
      <div style={styles.footer}>
        <button style={styles.btnBack} onClick={onBack}>
          ← Назад
        </button>
        <button 
          style={styles.btnSkip} 
          onClick={handleSkipPhotos}
          title="Продолжить без всех фото"
        >
          Пропустить фото
        </button>
        <button 
          style={{...styles.btnNext, ...(canProceed ? {} : styles.btnDisabled)}} 
          onClick={onNext}
          disabled={!canProceed}
        >
          Далее: AI Генерация → ({recipients.length})
        </button>
      </div>

      {/* Модальное окно загрузки фото */}
      {modalOpen && selectedRecipient && (
        <div style={styles.modal} onClick={() => setModalOpen(false)}>
          <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>
              📷 Фото для {selectedRecipient.name}
            </h3>
            
            {/* URL */}
            <div>
              <label style={{ fontSize: 13, color: '#6b7280', marginBottom: 6, display: 'block' }}>
                Вставьте ссылку на фото:
              </label>
              <input 
                style={styles.modalInput}
                type="url"
                placeholder="https://example.com/photo.jpg"
                value={photoUrl}
                onChange={(e) => setPhotoUrl(e.target.value)}
              />
              <button 
                style={{...styles.actionBtn, ...styles.searchBtn, width: '100%', justifyContent: 'center'}}
                onClick={handleUrlSubmit}
                disabled={uploading || !photoUrl.trim()}
              >
                {uploading ? '⏳ Сохранение...' : '💾 Сохранить URL'}
              </button>
            </div>
            
            <div style={styles.modalDivider}>— или —</div>
            
            {/* Загрузка файла */}
            <div 
              style={{
                ...styles.uploadZone,
                ...(dragOver === 'modal' ? styles.uploadZoneActive : {})
              }}
              onDragOver={(e) => { e.preventDefault(); setDragOver('modal'); }}
              onDragLeave={() => setDragOver(null)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(null);
                const file = e.dataTransfer.files[0];
                if (file) handleFileUpload(selectedRecipient.id, file);
              }}
              onClick={() => fileInputRef.current?.click()}
            >
              <div style={{ fontSize: 32, marginBottom: 8 }}>📁</div>
              <div style={{ fontWeight: 500, marginBottom: 4 }}>
                Перетащите файл сюда
              </div>
              <div style={{ fontSize: 13, color: '#9ca3af' }}>
                или нажмите для выбора (макс. 5MB)
              </div>
              <input 
                ref={fileInputRef}
                type="file" 
                accept="image/*"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleFileUpload(selectedRecipient.id, file);
                }}
              />
            </div>
            
            <div style={styles.modalBtns}>
              <button 
                style={{...styles.btnBack, flex: 1}}
                onClick={() => setModalOpen(false)}
              >
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Превью фото */}
      {previewOpen && (
        <div style={styles.preview} onClick={() => setPreviewOpen(false)}>
          <img src={previewUrl} alt="Preview" style={styles.previewImg} />
        </div>
      )}
    </div>
  );
};

export default CampaignPhotoReviewStep;